import { useEffect, useState } from 'react';
import { BOOKING_URL } from '../../lib/constants';
import { Menu, X, Sparkles } from 'lucide-react';

const NAV_LINKS = [
  { href: '#systems', label: 'Treatments' },
  { href: '#team', label: 'Team' },
  { href: '#pricing', label: 'Pricing' },
  { href: '#process', label: 'Process' },
  { href: '#reviews', label: 'Reviews' },
  { href: '#faq', label: 'FAQ' },
  { href: '#location', label: 'Location' },
];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(true);

  useEffect(() => {
    // Persist banner dismissal for the session
    if (typeof window !== 'undefined') {
      const dismissed = sessionStorage.getItem('promo-banner-dismissed');
      if (dismissed === '1') setBannerOpen(false);
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeBanner = () => {
    setBannerOpen(false);
    try { sessionStorage.setItem('promo-banner-dismissed', '1'); } catch (_) {}
  };

  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 left-0 right-0 z-50"
    >
      {/* Promo banner */}
      {bannerOpen && (
        <div
          data-testid="promo-banner"
          className="bg-gold-fill text-brand-ink relative border-b border-brand-gold/40"
        >
          <div className="container-prep py-2.5 sm:py-3 flex items-center justify-center gap-2 sm:gap-4 pr-9 sm:pr-12">
            <Sparkles size={14} className="hidden sm:inline shrink-0" />
            <p className="text-[11.5px] sm:text-sm font-medium tracking-tight sm:tracking-wide text-center leading-tight">
              New Clients Get <span className="font-bold">15% Off</span>
              <span className="hidden sm:inline"> — Use Code: </span>
              <span className="sm:hidden"> · </span>
              <span className="font-bold tracking-wider">PREP15</span>
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="promo-banner-book"
              className="hidden sm:inline-flex items-center gap-1 px-3.5 py-1 bg-brand-ink text-brand-cream rounded-full text-xs font-medium hover:bg-brand-bgSoft transition whitespace-nowrap"
            >
              Book Now →
            </a>
          </div>
          <button
            type="button"
            onClick={closeBanner}
            aria-label="Dismiss promo"
            data-testid="promo-banner-close"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-brand-ink/70 hover:text-brand-ink hover:bg-brand-ink/10 transition"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {/* Nav bar */}
      <div
        className={`transition-all duration-500 ${
          scrolled
            ? 'bg-brand-bg/85 backdrop-blur-xl border-b border-brand-gold/15 py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="container-prep flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="flex items-baseline gap-2 group">
            <span className="font-serif italic text-2xl text-brand-cream">YUMI Lash Lift</span>
            <span className="font-serif text-xl text-brand-gold tracking-wide">Vancouver</span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-medium text-brand-cream/85 hover:text-brand-gold transition-colors tracking-wide"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-now-button"
              className="hidden sm:inline-flex btn-primary !px-6 !py-3 text-xs"
            >
              Book Now
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 text-brand-cream"
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-brand-bg/95 backdrop-blur-xl border-t border-brand-gold/15">
            <div className="container-prep py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-brand-cream hover:text-brand-gold transition-colors"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-2"
                data-testid="nav-mobile-book-button"
              >
                Book Your Lash Lift
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navigation;
